/**
 * Build a family's first-3-invoices preview for the review step, feeding the
 * shared enrollments `InvoicePreview` widget. The first invoice is prorated when
 * the (batch) plan start date lands partway through the period; the next two are
 * full periods.
 *
 * @handoff Preview only. Proration and due dates are approximated here (day-based)
 * so providers see the shape before confirming; the exact figures are produced by
 * the payments engine (`enrollmentsUtils`) at invoice time.
 */
import dayjs, { Dayjs } from 'dayjs';
import { BillingIntervalEnum } from '../../enrollments/enums';
import type { InvoicePreview } from '../../enrollments/types';
import type { RosterRow } from '../AddFamilies/types';

const INTERVAL_MAP: Record<RosterRow['billingFrequency'], BillingIntervalEnum> = {
  weekly: BillingIntervalEnum.WEEKLY,
  biweekly: BillingIntervalEnum.BIWEEKLY,
  monthly: BillingIntervalEnum.MONTHLY,
};

const money = (v: string) => Number((v || '').replace(/[^0-9.]/g, '')) || 0;

function periodEnd(start: Dayjs, freq: RosterRow['billingFrequency']): Dayjs {
  if (freq === 'monthly') return start.endOf('month');
  if (freq === 'biweekly') return start.add(13, 'day');
  return start.add(6, 'day');
}

function periodLength(start: Dayjs, freq: RosterRow['billingFrequency']): number {
  if (freq === 'monthly') return start.daysInMonth();
  if (freq === 'biweekly') return 14;
  return 7;
}

export function buildFamilyPreview(row: RosterRow, startDate: string, billFor: RosterRow['billFor']): InvoicePreview[] {
  const amount = money(row.billingAmount);
  if (!startDate || amount <= 0) return [];

  const freq = row.billingFrequency;
  const interval = INTERVAL_MAP[freq];
  const advance = billFor !== 'prior';
  const invoices: InvoicePreview[] = [];

  let start = dayjs(startDate);
  for (let i = 0; i < 3; i++) {
    const end = periodEnd(start, freq);
    let amt = amount;
    let prorated = false;

    // Only the first invoice can be a partial period.
    if (i === 0) {
      const covered = end.diff(start, 'day') + 1;
      const full = periodLength(start, freq);
      if (covered < full) {
        prorated = true;
        amt = Math.round((amount * covered) / full);
      }
    }

    // First invoice is due on the start date; after that, in advance = due just
    // before the period starts, in arrears = due at the period's end.
    const due = i === 0 ? start : advance ? start.subtract(1, 'day') : end;

    invoices.push({
      label: start.format('MMMM'),
      serviceStart: start.toDate(),
      serviceEnd: end.toDate(),
      dueDate: due.toDate(),
      amount: amt,
      isProrated: prorated,
      billingInterval: interval,
    });

    start = end.add(1, 'day');
  }

  return invoices;
}
